import React, { useMemo } from 'react';

const getStat = (statsData, group, key) => {
  const split = statsData?.stats?.find(s => s.group?.displayName === group)?.splits?.[0]?.stat;
  return parseFloat(split?.[key]) || 0;
};

const RatingBar = ({ label, awayScore, homeScore, awayAbbr, homeAbbr }) => {
  const total = awayScore + homeScore || 1;
  const awayPct = (awayScore / total) * 100;
  const homePct = (homeScore / total) * 100;
  const awayWins = awayScore >= homeScore;

  return (
    <div className="flex flex-col gap-1 py-2 border-b border-[rgba(101,141,113,0.2)] last:border-0">
      <div className="flex justify-between items-center">
        <span className="text-[9px] uppercase tracking-widest text-[#658d71] font-bold">{label}</span>
        <div className="flex gap-3 text-[9px] font-black">
          <span className={awayWins ? 'text-[#a9c9b1]' : 'text-[#658d71]'}>{awayAbbr} {awayScore.toFixed(1)}</span>
          <span className={!awayWins ? 'text-[#c4333e]' : 'text-[#658d71]'}>{homeAbbr} {homeScore.toFixed(1)}</span>
        </div>
      </div>
      <div className="flex h-3 rounded-sm overflow-hidden">
        <div className="h-full transition-all duration-700" style={{ width: `${awayPct}%`, backgroundColor: '#a9c9b1', opacity: 0.8 }} />
        <div className="h-full transition-all duration-700" style={{ width: `${homePct}%`, backgroundColor: '#c4333e', opacity: 0.8 }} />
      </div>
    </div>
  );
};

const MatchupPowerRating = ({ awayStats, homeStats, awayAbbr, homeAbbr }) => {
  const ratings = useMemo(() => {
    const awayOPS = getStat(awayStats, 'hitting', 'ops');
    const homeOPS = getStat(homeStats, 'hitting', 'ops');
    const awayERA = getStat(awayStats, 'pitching', 'era');
    const homeERA = getStat(homeStats, 'pitching', 'era');
    const awayWHIP = getStat(awayStats, 'pitching', 'whip');
    const homeWHIP = getStat(homeStats, 'pitching', 'whip');
    const awayAvg = getStat(awayStats, 'hitting', 'avg');
    const homeAvg = getStat(homeStats, 'hitting', 'avg');
    const awayHR = getStat(awayStats, 'hitting', 'homeRuns');
    const homeHR = getStat(homeStats, 'hitting', 'homeRuns');

    const awayOffense = (awayOPS * 50 + awayAvg * 100 + awayHR * 0.1).toFixed(1);
    const homeOffense = (homeOPS * 50 + homeAvg * 100 + homeHR * 0.1).toFixed(1);
    const awayPitching = awayERA > 0 ? (10 - awayERA - awayWHIP).toFixed(1) : '0.0';
    const homePitching = homeERA > 0 ? (10 - homeERA - homeWHIP).toFixed(1) : '0.0';

    const awayTotal = (parseFloat(awayOffense) + parseFloat(awayPitching)).toFixed(1);
    const homeTotal = (parseFloat(homeOffense) + parseFloat(homePitching)).toFixed(1);

    return { awayOffense, homeOffense, awayPitching, homePitching, awayTotal, homeTotal };
  }, [awayStats, homeStats]);

  const totalA = parseFloat(ratings.awayTotal);
  const totalH = parseFloat(ratings.homeTotal);
  const totalSum = totalA + totalH || 1;
  const awayWinPct = Math.round((totalA / totalSum) * 100);
  const homeWinPct = 100 - awayWinPct;

  return (
    <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)]">
      <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
        <span className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">Power Rating</span>
        <p className="text-[9px] text-[#658d71] font-bold uppercase tracking-widest mt-0.5">Season-based matchup edge</p>
      </div>
      <div className="p-3 flex flex-col gap-2">
        <RatingBar label="Offense" awayScore={parseFloat(ratings.awayOffense)} homeScore={parseFloat(ratings.homeOffense)} awayAbbr={awayAbbr} homeAbbr={homeAbbr} />
        <RatingBar label="Pitching" awayScore={parseFloat(ratings.awayPitching)} homeScore={parseFloat(ratings.homePitching)} awayAbbr={awayAbbr} homeAbbr={homeAbbr} />
        <RatingBar label="Overall" awayScore={totalA} homeScore={totalH} awayAbbr={awayAbbr} homeAbbr={homeAbbr} />

        <div className="mt-2 border border-[#658d71] p-3 bg-[rgba(0,0,0,0.1)]">
          <p className="text-[9px] uppercase tracking-widest text-[#658d71] font-bold mb-2 text-center">Projected Win %</p>
          <div className="flex h-5 rounded-sm overflow-hidden mb-2">
            <div className="h-full flex items-center justify-center text-[9px] font-black text-white" style={{ width: `${awayWinPct}%`, backgroundColor: 'rgba(169,201,177,0.7)' }}>
              {awayWinPct > 15 && `${awayWinPct}%`}
            </div>
            <div className="h-full flex items-center justify-center text-[9px] font-black text-white" style={{ width: `${homeWinPct}%`, backgroundColor: 'rgba(196,51,62,0.7)' }}>
              {homeWinPct > 15 && `${homeWinPct}%`}
            </div>
          </div>
          <div className="flex justify-between text-[9px] font-black uppercase tracking-widest">
            <span className="text-[#a9c9b1]">{awayAbbr} {awayWinPct}%</span>
            <span className="text-[#c4333e]">{homeAbbr} {homeWinPct}%</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchupPowerRating;